"use client";

/**
 * BlurTransition Component
 *
 * Premium step transition with Stripe/Linear-inspired animation.
 * Uses vertical (Y-axis) movement for a more natural "rising" feel.
 *
 * Animation formula (based on industry research):
 * - Direction: Vertical (bottom-up entry, fade-up exit)
 * - Blur reveal: 8px → 0px (Linear's signature technique)
 * - Scale: 0.98 → 1 (subtle polish, elements "land" in place)
 * - Duration: 350ms (snappy but smooth)
 * - Easing: ease-out for entry (fast start, soft landing)
 *
 * This creates the "wow factor" of Stripe/OpenAI onboarding flows.
 */
import { c as _c } from "react/compiler-runtime";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { ReactNode } from "react";
interface BlurTransitionProps {
  children: ReactNode;
  /** Unique key for AnimatePresence (typically the step number) */
  keyValue: string | number;
  /** Direction of transition: 1 = forward, -1 = backward */
  direction?: number;
  /** Skip animation entirely - content appears instantly */
  skipAnimation?: boolean;
  /** Optional className for the wrapper */
  className?: string;
}

// Premium ease curves (industry standard)
const EASE_OUT = [0.22, 1, 0.36, 1] as const; // Smooth deceleration - elements "land"
const EASE_IN = [0.4, 0, 1, 1] as const; // Quick acceleration for exits

// Animation variants - vertical movement with blur reveal + scale
// Transitions are embedded in variants for proper TypeScript support
const variants: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    filter: "blur(8px)",
    y: direction > 0 ? 24 : -16,
    // Forward: rise from below, Back: drop from above
    scale: 0.98,
    willChange: "transform, opacity, filter"
  }),
  active: {
    opacity: 1,
    filter: "blur(0px)",
    y: 0,
    scale: 1,
    willChange: "auto",
    transition: {
      duration: 0.35,
      ease: EASE_OUT
    }
  },
  exit: (direction: number) => ({
    opacity: 0,
    filter: "blur(6px)",
    y: direction > 0 ? -12 : 16,
    // Forward: float up, Back: drop down
    scale: 0.99,
    willChange: "transform, opacity, filter",
    transition: {
      duration: 0.25,
      ease: EASE_IN
    }
  })
};
export default function BlurTransition(t0) {
  const $ = _c(12);
  const {
    children,
    keyValue,
    direction: t1,
    skipAnimation: t2,
    className: t3
  } = t0;
  const direction = t1 === undefined ? 1 : t1;
  const skipAnimation = t2 === undefined ? false : t2;
  const className = t3 === undefined ? "" : t3;
  if (skipAnimation) {
    const t4 = `w-full ${className}`;
    let t5;
    if ($[0] !== children || $[1] !== keyValue || $[2] !== t4) {
      t5 = <div key={keyValue} className={t4}>{children}</div>;
      $[0] = children;
      $[1] = keyValue;
      $[2] = t4;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    return t5;
  }
  const t4 = `w-full ${className}`;
  let t5;
  if ($[4] !== children || $[5] !== direction || $[6] !== keyValue || $[7] !== t4) {
    t5 = <motion.div key={keyValue} custom={direction} variants={variants} initial="enter" animate="active" exit="exit" className={t4}>{children}</motion.div>;
    $[4] = children;
    $[5] = direction;
    $[6] = keyValue;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== direction || $[10] !== t5) {
    t6 = <AnimatePresence mode="wait" custom={direction}>{t5}</AnimatePresence>;
    $[9] = direction;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/**
 * BlurTransitionItem Component
 *
 * A simplified wrapper that handles the AnimatePresence externally.
 * Use this when you need more control over the animation lifecycle.
 */
export function BlurTransitionItem(t0) {
  const $ = _c(4);
  const {
    children,
    direction: t1,
    className: t2
  } = t0;
  const direction = t1 === undefined ? 1 : t1;
  const className = t2 === undefined ? "" : t2;
  const t3 = `w-full ${className}`;
  let t4;
  if ($[0] !== children || $[1] !== direction || $[2] !== t3) {
    t4 = <motion.div custom={direction} variants={variants} initial="enter" animate="active" exit="exit" className={t3}>{children}</motion.div>;
    $[0] = children;
    $[1] = direction;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}